import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import LandingFooter from "../components/LandingFooter";
import PricingSection from "../components/PricingSection";
import { trackGoal } from "../utils/datafast";
import useFocusTrap from "../hooks/useFocusTrap";
import "./PublicSite.css";

const LOGO_WHITE_PUBLIC_PATH = "/clearbadge-logo-white.png";

const TRIAL_INVITE_CONFIG = {
  pro: {
    tierId: "pro",
    trialDays: 7,
    signupPath: "/login?mode=signup&plan=pro&interval=monthly",
    viewGoal: "view_trial_invite",
    navCta: "Start FREE 7-Day Trial",
    cardDescription: "You're invited to start a 7-day free trial of Pro and unlock the full ClearBadge experience.",
    cardCta: "Start FREE 7-Day Trial",
    cardBadge: "Trial Invite",
    heroTitle: "You're Invited to Try Pro Free for 7 Days",
    heroDescription: "Activate your invitation, explore every Pro feature, and decide before your trial ends.",
    getStartedEyebrow: "LIMITED INVITE",
    getStartedTitle: "See what Pro can do before you commit.",
    getStartedDescription:
      "Your invite unlocks seven days of Pro so you can test unlimited license verification, enhanced profile tools, lead capture, and live Trust Sessions without paying upfront.",
  },
  elite: {
    tierId: "elite",
    trialDays: 7,
    signupPath: "/login?mode=signup&plan=elite&interval=monthly",
    viewGoal: "view_elite_trial_invite",
    navCta: "Start FREE Elite Trial",
    cardDescription:
      "You're invited to start a 7-day free trial of Elite and unlock ClearBadge's highest visibility tier.",
    cardCta: "Start FREE Elite Trial",
    cardBadge: "Elite Trial Invite",
    heroTitle: "You're Invited to Try Elite Free for 7 Days",
    heroDescription: "Activate your invitation, explore every Elite feature, and decide before your trial ends.",
    getStartedEyebrow: "ELITE INVITE",
    getStartedTitle: "See what Elite can do before you commit.",
    getStartedDescription:
      "Your invite unlocks seven days of Elite so you can test premium visibility, premium analytics, secure links, vanity URLs, and extended Trust Sessions without paying upfront.",
  },
};

const buildTierOverrides = (config) => ({
  free: {
    description: "Stay on the free plan if you want the essentials and decide later.",
    cta: "Keep Free Access",
  },
  [config.tierId]: {
    description: config.cardDescription,
    pricingNote: `Start your ${config.trialDays}-day free trial today. Cancel before renewal anytime.`,
    cta: config.cardCta,
    badge: config.cardBadge,
  },
});

export default function TrialInvitePage({ tier = "pro" }) {
  const config = TRIAL_INVITE_CONFIG[tier] || TRIAL_INVITE_CONFIG.pro;
  const tierOverrides = buildTierOverrides(config);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(true);
  const landingRootRef = useRef(null);
  const mobileDrawerRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const isVerifyPage = location.pathname === "/";
  const isFindProPage =
    location.pathname.startsWith("/find-a-pro") || location.pathname.startsWith("/find-pro");
  const isPricingPage =
    location.pathname.startsWith("/pricing") ||
    location.pathname.startsWith("/trial-invite") ||
    location.pathname.startsWith("/elite-trial-invite");
  const isAboutPage = location.pathname.startsWith("/about");
  const isPrivacyPage = location.pathname.startsWith("/privacy-policy");
  const isTermsPage = location.pathname.startsWith("/terms-of-service");

  useFocusTrap({
    isOpen: mobileMenuOpen,
    containerRef: mobileDrawerRef,
    onClose: () => setMobileMenuOpen(false),
    returnFocusRef: mobileMenuButtonRef,
    lockScroll: true,
  });

  useEffect(() => {
    trackGoal(config.viewGoal);
  }, [config.viewGoal]);

  useEffect(() => {
    const rootNode = landingRootRef.current;
    if (!rootNode) return undefined;
    const revealNodes = rootNode.querySelectorAll("[data-reveal]");
    if (!revealNodes.length) return undefined;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -10% 0px" }
    );

    revealNodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    lastScrollYRef.current = window.scrollY || 0;

    const onScroll = () => {
      const currentY = window.scrollY || 0;
      const previousY = lastScrollYRef.current;
      const delta = currentY - previousY;
      if (Math.abs(delta) < 6) return;

      if (currentY <= 24) {
        setShowFloatingNav(true);
      } else if (delta > 0) {
        setShowFloatingNav(false);
      } else {
        setShowFloatingNav(true);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) setShowFloatingNav(true);
  }, [mobileMenuOpen]);

  return (
    <div ref={landingRootRef} className="public-shell landing pricing-page clearbadge-public trial-invite-page">
      <header className={`public-topbar landing-topbar ${showFloatingNav ? "" : "landing-topbar--hidden"}`}>
        <div className="landing-topbar-inner">
          <div className="landing-topbar-left">
            <Link to="/" className="brand-inline" aria-label="ClearBadge home">
              <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
            </Link>
            <nav className="public-search-nav" aria-label="Search navigation">
              <Link
                className={`top-link ${location.pathname === "/" ? "active" : ""}`}
                to="/"
                data-reveal
                style={{ "--reveal-delay": "560ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Verify a License
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/find-a-pro") ? "active" : ""}`}
                to="/find-a-pro"
                data-reveal
                style={{ "--reveal-delay": "700ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Find a Pro
              </Link>
              <Link
                className={`top-link ${isPricingPage ? "active" : ""}`}
                to="/pricing"
                data-reveal
                style={{ "--reveal-delay": "840ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Pricing
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/about") ? "active" : ""}`}
                to="/about"
                data-reveal
                style={{ "--reveal-delay": "980ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                About
              </Link>
            </nav>
          </div>
          <div className="public-links landing-topbar-right">
            <Link
              className="top-link"
              to="/login"
              data-reveal
              style={{ "--reveal-delay": "1120ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
            >
              Sign In
            </Link>
            <Link
              className="primary nav-cta mobile-signup"
              to={config.signupPath}
              data-reveal
              style={{ "--reveal-delay": "1260ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
            >
              {config.navCta}
            </Link>
            <button
              ref={mobileMenuButtonRef}
              className="icon-btn mobile-menu-btn"
              type="button"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="trial-invite-mobile-menu"
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              <span className="burger-line" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div className={`public-mobile-overlay ${mobileMenuOpen ? "open" : ""}`}>
        <button
          type="button"
          className="public-mobile-overlay-dismiss"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close menu"
        />
        <div
          id="trial-invite-mobile-menu"
          ref={mobileDrawerRef}
          className={`public-mobile-drawer ${mobileMenuOpen ? "open" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="public-mobile-header">
            <Link to="/" className="brand-inline" aria-label="ClearBadge home" onClick={() => setMobileMenuOpen(false)}>
              <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
            </Link>
            <button className="icon-btn mobile-drawer-close" type="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
              <span className="mobile-drawer-close-icon" aria-hidden="true" />
            </button>
          </div>
          <div className="public-mobile-links">
            <Link to="/login" className="public-mobile-link" onClick={() => setMobileMenuOpen(false)}>
              Sign In
            </Link>
            <Link
              to={config.signupPath}
              className="public-mobile-link primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              {config.navCta}
            </Link>
            <div className="mobile-menu-section">
              <span className="mobile-menu-label">TOOLS</span>
              <Link
                to="/"
                className={`public-mobile-link secondary ${isVerifyPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Verify a License</span>
              </Link>
              <Link
                to="/find-a-pro"
                className={`public-mobile-link secondary ${isFindProPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Find a Pro</span>
              </Link>
            </div>
            <div className="mobile-menu-section">
              <span className="mobile-menu-label">RESOURCES</span>
              <Link
                to="/about"
                className={`public-mobile-link secondary ${isAboutPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">About</span>
              </Link>
              <Link
                to="/pricing"
                className={`public-mobile-link secondary ${isPricingPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Pricing</span>
              </Link>
              <Link
                to="/privacy-policy"
                className={`public-mobile-link secondary ${isPrivacyPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Privacy Policy</span>
              </Link>
              <Link
                to="/terms-of-service"
                className={`public-mobile-link secondary ${isTermsPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Terms of Service</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <main className="landing-main pricing-main" id="main-content">
        <PricingSection
          className="pricing-section-core"
          tierIds={["free", config.tierId]}
          tierOverrides={tierOverrides}
          header={(
            <div className="pricing-page-hero-head">
              <h1 data-reveal style={{ "--reveal-delay": "1460ms" }}>{config.heroTitle}</h1>
              <p data-reveal style={{ "--reveal-delay": "1600ms" }}>
                {config.heroDescription}
              </p>
            </div>
          )}
        />

        <section className="get-started-section pricing-page-get-started" id="get-started">
          <div className="landing-container">
            <div className="get-started-card" data-reveal>
              <div className="get-started-copy">
                <p className="eyebrow">{config.getStartedEyebrow}</p>
                <h2>{config.getStartedTitle}</h2>
                <p>
                  {config.getStartedDescription}
                </p>
              </div>
              <div className="get-started-actions">
                <Link className="primary get-started-primary" to={config.signupPath}>
                  {config.cardCta}
                </Link>
                <Link className="get-started-secondary" to="/login?mode=signup">
                  Stay on Free
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
